// tratto della falda sulla retta u = costante (perpendicolare alla gronda), in coordinate del campo
export function sectionSpan(f, uc) {
  const vs = [], P = f.polyUV;
  for (let i = 0; i < P.length; i++) {
    const p = P[i], q = P[(i + 1) % P.length];
    if ((p[0] - uc) * (q[0] - uc) > 0 || p[0] === q[0]) continue;
    vs.push(p[1] + ((uc - p[0]) / (q[0] - p[0])) * (q[1] - p[1]));
  }
  return vs.length < 2 ? null : [Math.min(...vs), Math.max(...vs)];
}

// linee di sezione disegnate sulle falde (una per falda), con lettere A, B, C...
export function sectionsOf(layout) {
  if (layout.flat) return [];
  return layout.fields.filter((f) => Number.isFinite(Number(f.roof.section?.u)) && sectionSpan(f, Number(f.roof.section.u)))
    .map((f, i) => ({ f, u: Number(f.roof.section.u), letter: f.roof.section.letter || String.fromCharCode(65 + (i % 26)) }))
    .sort((a, b) => a.letter.localeCompare(b.letter));
}

// lettera stabile per una nuova linea di sezione: la prima non usata dalle altre falde
export function nextSectionLetter(roofs, rid) {
  const own = roofs.find((r) => r.id === rid)?.section?.letter;
  if (own) return own;
  const used = new Set(roofs.filter((r) => r.id !== rid && r.section?.letter).map((r) => r.section.letter));
  for (let i = 0; i < 26; i++) { const L = String.fromCharCode(65 + i); if (!used.has(L)) return L; }
  return "Z";
}

// blocchi di moduli contigui (adiacenza riga/colonna) con ingombro: larghezza lungo gronda, lunghezza vera lungo falda
// tetti piani: ogni fila è un gruppo, spezzata dove mancano moduli (es. un camino)
function rowGroups(f) {
  const rows = new Map();
  for (const c of f.active) { const k = `${c.a || 0}:${c.r}`; rows.set(k, [...(rows.get(k) || []), c]); }
  const out = [];
  for (const [r, cells] of [...rows.entries()].sort((a, b) => a[1][0].a - b[1][0].a || a[1][0].r - b[1][0].r)) {
    cells.sort((a, b) => a.c - b.c);
    let run = [cells[0]];
    const flush = () => {
      const u0 = Math.min(...run.map((c) => c.rect[0])), u1 = Math.max(...run.map((c) => c.rect[2]));
      const v0 = Math.min(...run.map((c) => c.rect[1])), v1 = Math.max(...run.map((c) => c.rect[3]));
      out.push({ row: r, a: run[0].a || 0, ri: run[0].r, n: run.length, u0, u1, v0, v1, rows: 1, cols: run.length, width: u1 - u0, length: v1 - v0, full: true, flatRow: true });
    };
    for (const c of cells.slice(1)) {
      if (c.c === run[run.length - 1].c + 1) run.push(c);
      else { flush(); run = [c]; }
    }
    flush();
  }
  return out;
}

// spazio libero tra file consecutive di un tetto piano (bordo posteriore → bordo anteriore della fila successiva)
export function rowGaps(f) {
  const g = rowGroups(f), byRow = new Map();
  for (const x of g) byRow.set(x.row, [...(byRow.get(x.row) || []), x]);
  const rows = [...byRow.keys()].sort((a, b) => byRow.get(a)[0].a - byRow.get(b)[0].a || byRow.get(a)[0].ri - byRow.get(b)[0].ri), out = [];
  for (let i = 0; i < rows.length - 1; i++) {
    const A = byRow.get(rows[i]), B = byRow.get(rows[i + 1]);
    if (A[0].a !== B[0].a) continue;
    const lo = Math.max(Math.min(...A.map((x) => x.u0)), Math.min(...B.map((x) => x.u0))), hi = Math.min(Math.max(...A.map((x) => x.u1)), Math.max(...B.map((x) => x.u1)));
    if (hi <= lo) continue;
    const va = Math.max(...A.map((x) => x.v1)), vb = Math.min(...B.map((x) => x.v0));
    if (vb - va > 0.005) out.push({ u: lo + Math.min(0.4, (hi - lo) / 2), va, vb, gap: vb - va });
  }
  return out;
}

export function moduleGroups(f, flat) {
  if (flat) return rowGroups(f);
  const cosA = Math.cos((f.pitch * Math.PI) / 180) || 1;
  const byKey = new Map(f.active.map((c) => [`${c.a || 0},${c.r},${c.c}`, c])), seen = new Set(), out = [];
  for (const c0 of f.active) {
    if (seen.has(c0.key)) continue;
    const comp = [], st = [c0];
    seen.add(c0.key);
    while (st.length) {
      const c = st.pop();
      comp.push(c);
      for (const [dr, dc] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const n = byKey.get(`${c.a || 0},${c.r + dr},${c.c + dc}`);
        if (n && !seen.has(n.key)) { seen.add(n.key); st.push(n); }
      }
    }
    const u0 = Math.min(...comp.map((c) => c.rect[0])), u1 = Math.max(...comp.map((c) => c.rect[2]));
    const v0 = Math.min(...comp.map((c) => c.rect[1])), v1 = Math.max(...comp.map((c) => c.rect[3]));
    out.push({ n: comp.length, u0, u1, v0, v1, rows: new Set(comp.map((c) => c.r)).size, cols: new Set(comp.map((c) => c.c)).size,
      width: u1 - u0, length: (v1 - v0) / cosA, full: comp.length === new Set(comp.map((c) => c.r)).size * new Set(comp.map((c) => c.c)).size });
  }
  return out;
}

// Sezione verticale della falda perpendicolare alla gronda (misure lungo falda in m); senza u: nel mezzo del campo moduli
export function sectionData(field, uAt) {
  const f = field, a = (f.pitch * Math.PI) / 180, cosA = Math.cos(a) || 1;
  if (!f.active.length && uAt === undefined) return null;
  let uc = uAt;
  if (uc === undefined) {
    const us = f.active.map((c) => (c.rect[0] + c.rect[2]) / 2).sort((x, y) => x - y);
    uc = us[Math.floor(us.length / 2)];
  }
  const span = sectionSpan(f, uc);
  if (!span) return null;
  const [v0, v1] = span, S = (v) => (v - v0) / cosA;
  const q = f.roof.rail_q || [0.25, 0.75];
  const rows = f.active.filter((c) => c.rect[0] <= uc + 1e-9 && c.rect[2] >= uc - 1e-9)
    .map((c) => ({ s0: S(c.rect[1]), s1: S(c.rect[3]) })).sort((x, y) => x.s0 - y.s0);
  const rails = rows.flatMap((r) => q.map((t) => r.s0 + (r.s1 - r.s0) * t));
  return { pitch: f.pitch, length: S(v1), run: v1 - v0, rise: (v1 - v0) * Math.tan(a), rows, rails, q, name: f.roof.name, placed: uAt !== undefined };
}
